import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { C_value } from '@app/shared/_models/common';
import { HrAbsence } from '@app/shared/_models/hr';
import {
	HR_ABSENCE_DURATION_CUSTOM, HR_ABSENCE_DURATION_DAY, HR_ABSENCE_DURATION_HALF,
} from '@app/shared/constants';
import { pickedDateStr, todayStr } from '@app/shared/time';
import { frenchHolidaysOf } from '../../hr-schedule-engine';
import { EffectiveSlot } from '../../hr-schedule-engine';
import { LeaveBreakdown, hrLeaveBreakdown, hrLeaveBreakdownLabel } from '../../hr-leave';
import { RcrAccount, isRcrType, rcrCostOfRequest, rcrLabel, rcrRequestIssue } from '../../hr-rcr';
import { AbsenceQuota } from '../../hr-absence-catalog';
import {
	QuotaAccount, absenceQuotaAccountOf, absenceQuotaIssue, absenceQuotaOf, absenceQuotaPeriodLabel,
	quotaDaysLabel,
} from '../../hr-absence-quota';
import { HR_LEAVE_RULES_DEFAULT, LeaveRules, isInTakingPeriod, takingPeriodOf } from '../../hr-leave-rights';

// Création — ou REQUALIFICATION — d'une demande d'absence. Le dialog ne poste rien : il rend la
// demande validée (dates « YYYY-MM-DD ») + approveNow (admin : maladie / absence constatée,
// approuvée dans la foulée).
//
// REQUALIFIER, c'est la troisième réponse du responsable. « Approuver » et « Refuser » supposent
// que la demande est bien qualifiée ; or ce qui arrive vraiment, c'est une absence injustifiée qui
// doit devenir un congé payé, ou deux jours d'enfant malade là où la convention n'en accorde qu'un.
// Refuser sèchement oblige le salarié à ressaisir ce que le responsable a déjà sous les yeux —
// alors que la demande est encore en attente, donc encore modifiable (garde serveur : state 0).
export interface LeaveDialogData {
	employees: { id: number; name: string }[];	// admin : tout le monde ; sinon : uniquement soi
	types: C_value[];														// référentiel c_values 23
	id_employee_default: number | null;
	isAdmin: boolean;
	// Repos hebdomadaires PROPOSÉS par employé (jours ISO 1..7), devinés du planning théorique
	// par la page appelante (hrGuessRestWeekdays). Absent ⇒ samedi-dimanche.
	restWeekdaysByEmployee?: Map<number, number[]>;
	// Règles de congés de l'établissement — période de prise, fractionnement (c_values 25).
	leaveRules?: LeaveRules;
	// Types d'absence relevant du congé PAYÉ : seuls eux ouvrent droit au fractionnement.
	paidLeaveTypes?: number[];
	// RÉCUPÉRATION : le compte de chaque salarié (heures sup mises en repos) et son planning
	// théorique, pour chiffrer ce que la demande coûte et refuser un dépassement.
	// Absents ⇒ aucun contrôle : le type « récupération » se comporte comme un congé ordinaire.
	rcrAccountByEmployee?: Map<number, RcrAccount>;
	plannedSlots?: EffectiveSlot[];
	// CONGÉS PLAFONNÉS (enfant malade rémunéré de la convention…) : les absences déjà posées et la
	// date d'embauche de chacun, de quoi dire ce qu'il reste du droit et si l'ancienneté est
	// atteinte. Absents ⇒ aucun contrôle de plafond.
	absencesByEmployee?: Map<number, HrAbsence[]>;
	hireDateByEmployee?: Map<number, string | null>;
	// REQUALIFICATION / CLÔTURE : l'absence que le responsable retouche au lieu de la trancher.
	// Présente ⇒ le dialog s'ouvre pré-rempli sur elle et rend son `id` ; absente ⇒ création.
	absence?: HrAbsence;
	// CONSTAT : « ce matin, il n'est pas là ». Le responsable note le fait du jour, souvent sans
	// savoir jusqu'à quand — le dialog s'ouvre alors sur aujourd'hui, fin inconnue, approuvé.
	constat?: boolean;
}

export interface LeaveDialogResult {
	// 0 en création, l'id de la demande retouchée en requalification.
	id: number;
	id_employee: number;
	typ: number;
	datestart: string;
	dateend: string;
	duration_kind: number;
	hours: number | null;
	rest_days: number[];
	comment: string | null;
	approveNow: boolean;
	// Renonciation aux jours de fractionnement : L3141-21 exige un accord individuel du salarié,
	// donc une trace — qui, quand — et pas une simple case cochée au passage.
	fractionation_waiver: boolean;
	// Moitié de journée visée par une demi-journée (`data.half`) : c'est elle, et elle seule,
	// que le planning masquera. Null pour les autres durées.
	half: 'morning' | 'afternoon' | null;
	// Fin INCONNUE (colonne `open` de hr_absence) : l'absence court jusqu'à aujourd'hui tant
	// qu'on ne l'a pas clôturée. `false` l'éteint — c'est ce que fait la clôture.
	open: boolean;
}

@Component({
	selector: 'app-hr-leave-dialog',
	standalone: true,
	imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule,
		MatButtonModule, MatCheckboxModule, MatRadioModule, MatTooltipModule, MatDatepickerModule, MatIconModule],
	templateUrl: './leave-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './leave-dialog.component.scss',
})
export class LeaveDialogComponent {

	public data: LeaveDialogData = inject(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<LeaveDialogComponent>);

	public readonly DURATION_DAY = HR_ABSENCE_DURATION_DAY;
	public readonly DURATION_HALF = HR_ABSENCE_DURATION_HALF;
	public readonly DURATION_CUSTOM = HR_ABSENCE_DURATION_CUSTOM;

	// Trois usages, un seul dialog : une DEMANDE (le salarié pose un congé), un CONSTAT (le
	// responsable note l'absence du jour, fin souvent inconnue), une REQUALIFICATION ou une
	// CLÔTURE (il retouche une ligne existante). Tous les contrôles — plafond, récupération,
	// fractionnement — sont les mêmes : c'est la raison de ne pas en faire trois écrans.
	public readonly isRequalification = !!this.data.absence;
	public readonly isConstat = !this.data.absence && !!this.data.constat;

	// SAISIE DIRECTE — le responsable RH ne s'adresse pas une demande à lui-même.
	//
	// Quand c'est LUI qui saisit — un congé accordé de vive voix, un arrêt maladie, une absence
	// constatée — il n'y a pas de demande : il y a une décision, prise au moment de la saisie. Le
	// dialog ne dit donc plus « Envoyer la demande », la ligne naît approuvée, et rien n'atterrit
	// dans « À traiter » — une file où il aurait retrouvé sa propre saisie à valider une seconde
	// fois. La demande reste le mode du SALARIÉ, qui pose ses congés pour lui-même.
	public readonly isDirectEntry = this.data.isAdmin && !this.data.absence;

	// L'absence retouchée est-elle EN COURS (fin inconnue) ? Alors on ne « requalifie » pas, on
	// clôture — et c'est ce mot-là que le responsable cherche des yeux.
	public readonly isClosing = !!this.data.absence?.open;

	public get dialogTitle(): string {
		if (this.isClosing) return 'Clôturer l\'absence';
		if (this.isRequalification) return 'Requalifier la demande';
		if (this.isConstat) return 'Absence constatée';
		if (this.isDirectEntry) return 'Saisir une absence';
		return 'Nouvelle demande d\'absence';
	}

	public get validateLabel(): string {
		if (this.isClosing) return 'Clôturer';
		if (this.isRequalification) return 'Requalifier';
		if (this.isConstat || this.isDirectEntry) return 'Enregistrer l\'absence';
		return 'Envoyer la demande';
	}

	public form = new FormGroup({
		id_employee: new FormControl<number | null>(this.data.id_employee_default),
		typ: new FormControl<number | null>(null),
		datestart: new FormControl<string | Date | null>(null),
		dateend: new FormControl<string | Date | null>(null),
		duration_kind: new FormControl<number>(HR_ABSENCE_DURATION_DAY),
		half: new FormControl<'morning' | 'afternoon'>('afternoon'),		// « je pars cet après-midi » est le cas courant
		hours: new FormControl<number | null>(null),
		comment: new FormControl<string>(''),
		approveNow: new FormControl<boolean>(false),
		fractionationWaiver: new FormControl<boolean>(false),
		// « Je ne sais pas jusqu'à quand » : la fin reste ouverte, l'absence court jusqu'à
		// aujourd'hui, et une deuxième saisie la refermera quand l'arrêt de travail arrivera.
		openEnd: new FormControl<boolean>(false),
	});
	public errorMessage = '';

	// Pastilles de repos hebdomadaire : cliquables, pré-cochées d'après le planning de la personne.
	public dayChips = [
		{ day: 1, label: 'Lun.' }, { day: 2, label: 'Mar.' }, { day: 3, label: 'Mer.' }, { day: 4, label: 'Jeu.' },
		{ day: 5, label: 'Ven.' }, { day: 6, label: 'Sam.' }, { day: 7, label: 'Dim.' },
	];
	public restDays = signal<Set<number>>(new Set([6, 7]));

	// Recalculé à chaque frappe : le décompte réel de la demande.
	private formTick = signal(0);
	public breakdown = computed<LeaveBreakdown>(() => {
		this.formTick();		// dépendance explicite : le formulaire n'est pas un signal
		const v = this.form.value;
		const datestart = pickedDateStr(v.datestart);
		const dateend = pickedDateStr(v.dateend ?? v.datestart);
		if (!datestart || !dateend) return hrLeaveBreakdown('', '', [], new Map());

		// Les fériés des années couvertes par la demande (une demande à cheval sur le 31/12).
		const holidays = new Map<string, string>();
		const y0 = +datestart.slice(0, 4), y1 = +dateend.slice(0, 4);
		for (let y = y0; y <= (isNaN(y1) ? y0 : y1); y++) {
			for (const [k, label] of frenchHolidaysOf(y)) holidays.set(k, label);
		}
		return hrLeaveBreakdown(datestart, dateend, Array.from(this.restDays()), holidays,
			+(v.duration_kind ?? HR_ABSENCE_DURATION_DAY), v.hours ?? null);
	});

	public breakdownLabel = computed(() => hrLeaveBreakdownLabel(this.breakdown(), this.typeName()));

	public leaveRules = (): LeaveRules => this.data.leaveRules ?? HR_LEAVE_RULES_DEFAULT;

	// La demande sort-elle de la période de prise du congé principal (1er mai → 31 octobre) ?
	// C'est ce qui ouvre le droit aux jours de fractionnement — ou la renonciation.
	//
	// On compte ces jours dès que l'un des trois réglages s'y intéresse, et plus seulement quand
	// l'établissement ATTRIBUE les jours : ne pas les attribuer n'est licite que si le salarié y a
	// renoncé, et c'est justement là que la case de renonciation compte le plus. La lier à
	// l'attribution la faisait disparaître au moment où elle devenait indispensable.
	public outOfTakingPeriod = computed<number>(() => {
		this.formTick();
		const rules = this.leaveRules();
		if (!rules.fractionation_enabled && !rules.fractionation_waiver_required && !rules.fractionation_waiver_blocking) return 0;
		const typ = this.form.get('typ')!.value;
		const paidTypes = this.data.paidLeaveTypes;
		if (paidTypes && paidTypes.length > 0 && (typ === null || !paidTypes.includes(typ))) return 0;
		return this.breakdown().days.filter(d => d.kind === 'leave' && !isInTakingPeriod(d.dat, rules)).length;
	});

	// « du 01/05 au 31/10 » — la période telle qu'elle est réglée pour cet établissement.
	public takingPeriodLabel = computed<string>(() => {
		this.formTick();
		const rules = this.leaveRules();
		const ref = this.breakdown().days[0]?.dat ?? todayStr();
		const p = takingPeriodOf(ref, rules);
		const fr = (d: string) => d.slice(8, 10) + '/' + d.slice(5, 7);
		return 'du ' + fr(p.from) + ' au ' + fr(p.to);
	});

	// Ce que la loi accorde : +2 jours à partir de 6 jours hors période, +1 entre 3 et 5.
	public fractionationDays = computed<number>(() => {
		const out = this.outOfTakingPeriod();
		return out >= 6 ? 2 : (out >= 3 ? 1 : 0);
	});

	// La renonciation se propose dès que l'un des deux réglages la réclame. Si l'établissement
	// BLOQUE la demande sans renonciation, la case doit être là quoi qu'il arrive : sans elle,
	// personne ne pourrait plus poser un congé hors période.
	public waiverOffered = computed<boolean>(() =>
		this.fractionationDays() > 0
		&& (this.leaveRules().fractionation_waiver_required || this.leaveRules().fractionation_waiver_blocking));

	// Le refus, quand l'établissement l'exige : des jours de fractionnement sont dus et personne n'y
	// a renoncé. En dessous de 3 jours hors période, aucun droit ne naît — il n'y a rien à
	// renoncer, et rien à bloquer.
	public waiverIssue = computed<string>(() => {
		this.formTick();
		if (!this.leaveRules().fractionation_waiver_blocking) return '';
		if (this.fractionationDays() === 0 || this.form.get('fractionationWaiver')!.value) return '';
		return 'Ces ' + this.outOfTakingPeriod() + ' jour(s) hors période ouvrent droit à '
			+ this.fractionationDays() + ' jour(s) de fractionnement : le salarié doit y renoncer '
			+ 'expressément pour que la demande soit enregistrée.';
	});

	// ============================================================
	// RÉCUPÉRATION — poser ses heures supplémentaires comme un congé
	// ============================================================

	// Le type choisi débite-t-il le compteur de récupération ?
	public isRcr = computed<boolean>(() => {
		this.formTick();
		return isRcrType(this.form.get('typ')!.value, this.data.types);
	});

	// Le compte du salarié SÉLECTIONNÉ : sur un écran d'admin, changer d'employé change de solde.
	public rcrAccount = computed<RcrAccount | null>(() => {
		this.formTick();
		const id = this.form.get('id_employee')!.value;
		return (id != null ? this.data.rcrAccountByEmployee?.get(id) : null) ?? null;
	});

	// Ce que la demande retire du planning — la vraie valeur de la récupération. Une demi-journée
	// ne vaut pas un forfait : jeudi après-midi peut faire 3 h chez l'un et 5 h chez l'autre.
	public rcrCost = computed<number>(() => {
		this.formTick();
		if (!this.isRcr()) return 0;
		const v = this.form.value;
		const datestart = pickedDateStr(v.datestart);
		const dateend = pickedDateStr(v.dateend ?? v.datestart);
		if (!datestart || !dateend) return 0;
		const id = v.id_employee;
		const slots = (this.data.plannedSlots ?? []).filter(s => s.id_employee === id);
		return rcrCostOfRequest({
			datestart, dateend,
			duration_kind: +(v.duration_kind ?? HR_ABSENCE_DURATION_DAY),
			hours: v.hours ?? null,
			data: { half: v.half },
		}, slots);
	});

	// Le motif de refus, s'il y en a un — affiché AVANT l'envoi, pas après.
	public rcrIssue = computed<string | null>(() => {
		const account = this.rcrAccount();
		if (!this.isRcr() || !account) return null;
		return rcrRequestIssue(this.rcrCost(), account);
	});

	public rcrLabel = rcrLabel;

	// ============================================================
	// CONGÉS PLAFONNÉS — « il vous reste un jour », dit AVANT d'envoyer
	// ============================================================
	//
	// Certains congés se comptent à l'unité : la CCN 1982 accorde un jour rémunéré pour enfant
	// malade, par salarié et par année civile. Le salarié doit voir son droit au moment où il le
	// pose, pas le découvrir dans un refus.

	// Le plafond du type choisi, s'il en a un (réglage de l'établissement, sinon celui de la branche).
	public quota = computed<AbsenceQuota | null>(() => {
		this.formTick();
		const typ = this.form.get('typ')!.value;
		return absenceQuotaOf(this.data.types.find(t => t.id === typ));
	});

	// Le compte du salarié SÉLECTIONNÉ sur ce type, à la date de début de la demande : changer
	// d'employé ou d'année change le droit disponible.
	//
	// La demande REQUALIFIÉE est retirée du compte : elle est encore en base, en attente, et se
	// compterait donc elle-même — « il reste 0 jour » sur la ligne qu'on est justement en train de
	// ramener à un jour.
	public quotaAccount = computed<QuotaAccount | null>(() => {
		this.formTick();
		const quota = this.quota();
		const id = this.form.get('id_employee')!.value;
		const typ = this.form.get('typ')!.value;
		const mine = id != null ? this.data.absencesByEmployee?.get(id) : null;
		if (!quota || id == null || typ == null || !mine) return null;

		const editedId = this.data.absence?.id ?? 0;
		const dat = pickedDateStr(this.form.value.datestart) || todayStr();
		return absenceQuotaAccountOf(
			quota, mine.filter(a => a.typ === typ && a.id !== editedId), dat,
			this.data.hireDateByEmployee?.get(id) ?? null, this.leaveRules(), this.holidaysAround(dat),
		);
	});

	// Le motif de refus, s'il y en a un — plafond atteint, ou ancienneté pas encore acquise.
	public quotaIssue = computed<string | null>(() => {
		const account = this.quotaAccount();
		if (!account) return null;
		return absenceQuotaIssue(this.breakdown().leave_days, account, this.typeName());
	});

	public quotaDaysLabel = quotaDaysLabel;
	public quotaPeriodLabel = absenceQuotaPeriodLabel;

	// « 2026-03-12 » → « 12/03/2026 ». Un dialog de congés n'a pas besoin d'un pipe de dates pour ça.
	public frDat(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4);
	}

	// Les fériés des trois années qui entourent une date : de quoi couvrir une période de décompte,
	// qu'elle soit civile ou à cheval sur deux années (période de référence des congés payés).
	private holidaysAround(dat: string): Map<string, string> {
		const holidays = new Map<string, string>();
		const year = +dat.slice(0, 4);
		for (let y = year - 1; y <= year + 1; y++) {
			for (const [k, label] of frenchHolidaysOf(y)) holidays.set(k, label);
		}
		return holidays;
	}

	constructor() {
		this.form.valueChanges.subscribe(() => this.formTick.update(n => n + 1));
		// Changer d'employé réaligne les repos proposés sur SON planning.
		this.form.get('id_employee')!.valueChanges.subscribe(id => this.applyDefaultRest(id));
		this.applyDefaultRest(this.data.id_employee_default);
		// Le responsable RH décide en saisissant : la ligne naît approuvée, sans repasser par une
		// file d'attente qui n'attend que lui.
		if (this.isDirectEntry) this.form.get('approveNow')!.setValue(true, { emitEvent: false });
		if (this.data.absence) this.prefill(this.data.absence);
		else if (this.isConstat) this.prefillConstat();
	}

	// « Ce matin, Jérôme n'est pas là. » Le responsable ne remplit qu'une chose — le motif, et
	// encore, souvent provisoire : le jour est aujourd'hui, la fin est inconnue, et le fait est
	// constaté, donc approuvé d'emblée (ce n'est pas une demande, il n'y a rien à valider).
	private prefillConstat(): void {
		const today = todayStr();		// jour MÉTIER — toISOString() donnerait le jour UTC (J-1 avant 2 h en été)
		this.form.patchValue({ datestart: today, dateend: today, openEnd: true, approveNow: true });
		this.formTick.update(n => n + 1);
	}

	// Requalification : le dialog s'ouvre sur la demande TELLE QU'ELLE A ÉTÉ FAITE. Le responsable
	// corrige ce qui doit l'être (le type, la période) et le reste part inchangé — c'est la demande
	// du salarié qu'on retouche, pas une nouvelle qu'on saisit à sa place.
	private prefill(a: HrAbsence): void {
		this.form.patchValue({
			id_employee: a.id_employee,
			typ: a.typ,
			datestart: a.datestart,
			dateend: a.dateend,
			duration_kind: a.duration_kind ?? HR_ABSENCE_DURATION_DAY,
			half: (a.data?.half === 'morning' || a.data?.half === 'afternoon') ? a.data.half : 'afternoon',
			hours: a.hours ?? null,
			comment: a.comment ?? '',
			// Requalifier EST la décision du responsable : la demande retouchée n'a pas à repasser
			// par une seconde validation. La case reste décochable pour rendre la main au salarié.
			approveNow: true,
			// CLÔTURER, c'est justement décocher cette case et donner la vraie date de fin : elle
			// s'ouvre donc décochée, avec la fin roulée à aujourd'hui que le serveur vient de rendre.
			openEnd: false,
		});
		// Les repos de la demande d'origine l'emportent sur ceux devinés du planning : ils font
		// partie du décompte que le salarié a posé (patchValue vient de les réaligner).
		if (a.rest_days?.length) this.restDays.set(new Set(a.rest_days));
		this.formTick.update(n => n + 1);
	}

	private applyDefaultRest(id: number | null | undefined): void {
		const guess = (id != null ? this.data.restWeekdaysByEmployee?.get(id) : null) ?? [6, 7];
		this.restDays.set(new Set(guess));
		this.formTick.update(n => n + 1);
	}

	typeName(): string {
		const typ = this.form.get('typ')!.value;
		return this.data.types.find(t => t.id === typ)?.name ?? '';
	}

	toggleRestDay(day: number): void {
		const next = new Set(this.restDays());
		next.has(day) ? next.delete(day) : next.add(day);
		this.restDays.set(next);
		this.formTick.update(n => n + 1);
	}

	// Une demi-journée doit tenir sur un seul jour : la fin s'aligne sur le début.
	onDurationKindChange(kind: number): void {
		if (kind === HR_ABSENCE_DURATION_HALF) {
			this.form.get('dateend')!.setValue(this.form.get('datestart')!.value);
		}
	}

	// Fin inconnue : la période se referme sur son premier jour. Sans cela, une plage saisie avant
	// de cocher la case continuait d'alimenter le décompte et les compteurs affichés, alors que
	// l'écran ne montrait plus qu'une date — on lisait des jours que personne n'avait demandés.
	onOpenEndChange(open: boolean): void {
		if (open) this.form.get('dateend')!.setValue(this.form.get('datestart')!.value);
	}

	// La saisie porte-t-elle sur UN SEUL jour ? Une demi-journée n'en couvre qu'un par définition,
	// et une absence à fin inconnue n'a pas de fin à choisir. Dans ces deux cas la plage n'aurait
	// aucun sens — on montre un calendrier de date unique.
	get singleDate(): boolean {
		return +(this.form.get('duration_kind')!.value ?? HR_ABSENCE_DURATION_DAY) === HR_ABSENCE_DURATION_HALF
			|| !!this.form.get('openEnd')!.value;
	}

	get days(): number {
		return HrAbsence.daysInclusive(pickedDateStr(this.form.get('datestart')!.value), pickedDateStr(this.form.get('dateend')!.value));
	}

	onValidate(): void {
		const v = this.form.value;
		const datestart = pickedDateStr(v.datestart);
		// Fin inconnue : elle vaut le jour de début — c'est le dernier jour CONNU. Le serveur la
		// fera courir jusqu'à aujourd'hui tant que l'absence n'est pas clôturée.
		const dateend = v.openEnd ? datestart : pickedDateStr(v.dateend ?? v.datestart);		// un seul jour : fin = début
		const kind = +(v.duration_kind ?? HR_ABSENCE_DURATION_DAY);
		if (!v.id_employee) { this.errorMessage = 'Choisissez l\'employé.'; return; }
		if (!v.typ) { this.errorMessage = 'Choisissez le type d\'absence.'; return; }
		if (!datestart || !dateend) { this.errorMessage = 'Choisissez les dates.'; return; }
		if (dateend < datestart) { this.errorMessage = 'La fin est avant le début.'; return; }
		if (kind === HR_ABSENCE_DURATION_HALF && datestart !== dateend) {
			this.errorMessage = 'Une demi-journée doit tenir sur un seul jour.'; return;
		}
		if (kind === HR_ABSENCE_DURATION_CUSTOM && !(v.hours && +v.hours > 0)) {
			this.errorMessage = 'Indiquez le nombre d\'heures.'; return;
		}
		// Récupération : on ne pose que ce qu'on a. Le contrôle est ici plutôt qu'au retour du
		// serveur — un refus après coup, sur un écran de congés, ne se comprend pas.
		const rcrIssue = this.rcrIssue();
		if (rcrIssue) { this.errorMessage = rcrIssue; return; }
		// Même raison pour les congés que la convention accorde au compte-gouttes.
		const quotaIssue = this.quotaIssue();
		if (quotaIssue) { this.errorMessage = quotaIssue; return; }
		// Fractionnement : l'établissement peut exiger la renonciation AVANT d'enregistrer. Le
		// refus se dit ici, avec ce qui le lève — la case est juste au-dessus.
		const waiverIssue = this.waiverIssue();
		if (waiverIssue) { this.errorMessage = waiverIssue; return; }
		this.dialogRef.close({
			id: this.data.absence?.id ?? 0,
			id_employee: v.id_employee,
			typ: v.typ,
			datestart, dateend,
			duration_kind: kind,
			hours: kind === HR_ABSENCE_DURATION_CUSTOM ? +v.hours! : null,
			rest_days: Array.from(this.restDays()).sort((a, b) => a - b),
			comment: v.comment?.trim() || null,
			approveNow: this.data.isAdmin && !!v.approveNow,
			fractionation_waiver: this.fractionationDays() > 0 && !!v.fractionationWaiver,
			half: kind === HR_ABSENCE_DURATION_HALF ? (v.half ?? 'afternoon') : null,
			open: !!v.openEnd,
		} as LeaveDialogResult);
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}
}
